export interface DiffSegment {
  text: string
  /** `same` appears in both sides; `unique` is only on this side. */
  kind: 'same' | 'unique'
}

export interface TokenDiff {
  /** Segments to render on the LLM side (left). */
  llm: DiffSegment[]
  /** Segments to render on the User side (right). */
  user: DiffSegment[]
}

/**
 * Word-level diff of two strings using the standard LCS recurrence on
 * whitespace-preserving tokens. Each side's output contains every token of its
 * own input tagged as `same` (present in both) or `unique` (only on this side).
 *
 * Token granularity is word-like: runs of non-whitespace are tokens, and the
 * whitespace between tokens is its own token so rendering preserves original
 * spacing.
 *
 * Intended for UI highlighting on `diff` rows of the LLM-vs-user drawer.
 */
export function diffTokens(llm: string, user: string): TokenDiff {
  const leftTokens = tokenize(llm)
  const rightTokens = tokenize(user)

  const lcs = buildLcs(leftTokens, rightTokens)

  return {
    llm: markSide(leftTokens, rightTokens, lcs, 'left'),
    user: markSide(leftTokens, rightTokens, lcs, 'right'),
  }
}

/** Matches either a run of whitespace or a run of non-whitespace (one token). */
const WHITESPACE_OR_WORD_MATCHER = /\s+|\S+/g

function tokenize(text: string): string[] {
  if (text === '') return []
  return text.match(WHITESPACE_OR_WORD_MATCHER) ?? []
}

/** Classic LCS length table, dimensions (n+1) x (m+1). */
function buildLcs(a: string[], b: string[]): number[][] {
  const n = a.length
  const m = b.length
  const table: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  )
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1])
      }
    }
  }
  return table
}

/**
 * Walk the LCS table to emit, for one side, each of its tokens tagged either
 * `same` (token is part of the LCS) or `unique` (skipped over by the LCS). The
 * walk goes back-to-front, then reverses and coalesces adjacent same-kind
 * segments.
 */
function markSide(
  left: string[],
  right: string[],
  lcs: number[][],
  side: 'left' | 'right'
): DiffSegment[] {
  const out: DiffSegment[] = []
  let i = left.length
  let j = right.length

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && left[i - 1] === right[j - 1]) {
      const token = side === 'left' ? left[i - 1] : right[j - 1]
      out.push({ text: token, kind: 'same' })
      i--
      j--
      continue
    }

    const up = i > 0 ? lcs[i - 1][j] : -1
    const leftward = j > 0 ? lcs[i][j - 1] : -1

    if (j === 0 || up >= leftward) {
      if (side === 'left' && i > 0) {
        out.push({ text: left[i - 1], kind: 'unique' })
      }
      i--
    } else {
      if (side === 'right' && j > 0) {
        out.push({ text: right[j - 1], kind: 'unique' })
      }
      j--
    }
  }

  return coalesce(out.reverse())
}

function coalesce(segments: DiffSegment[]): DiffSegment[] {
  const out: DiffSegment[] = []
  let current: DiffSegment | null = null
  for (const seg of segments) {
    if (current && current.kind === seg.kind) {
      current.text += seg.text
      continue
    }
    if (current) out.push(current)
    current = { ...seg }
  }
  if (current) out.push(current)
  return out
}
